export function findWhere(list, properties) {
    return list.find(item => Object.keys(properties).every(key => Reflect.has(item, key) && item[key] === properties[key]));
}
/**
 * Find the last index in an Array
 *
 * @param array - The Array
 *
 * @example
 * ```typescript
 * // To find the last index in an array
 * last(array);
 *
 * ```
 */
export function last(array) {
    if (array.length === 0) {
        return undefined; // return undefined for an empty array
    }
    return array[array.length - 1];
}
/**
 * Identify if a value is an Array
 *
 * @param value - The item we are checking against
 *
 * @example
 * ```typescript
 * // To check if the input is an array
 * isArray([test1, test2]);
 *
 * ```
 */
export function isArray(value) {
    return Array.isArray(value);
}
/**
 * Returns an array of distinct elements from the given array.
 *
 * @param {any[]} arr - The input array.
 * @param {function} [iteratee=(item) => item] - The function used to map each element before checking for distinctness.
 * @param returnIterateeValue
 * @returns {T[]} - An array of distinct elements.
 */
export function distinct(arr, iteratee = (item) => item, returnIterateeValue = true) {
    const seen = new Set();
    const filtered = arr.filter(item => {
        const val = iteratee(item);
        if (seen.has(val)) {
            return false;
        }
        seen.add(val);
        return true;
    });
    if (!returnIterateeValue) {
        return filtered;
    }
    else {
        return filtered.map(item => iteratee(item));
    }
}
/**
 * Returns sorted array based on value to sort on and value for ascending/descending.
 *
 * @param  arr - The input array.
 * @param columnId - The key of the values being sorted on
 * @param direction - The way the data is to be sorted
 * @returns {T[]} - An array of distinct elements.
 */
export function sortBy(arr, columnId, direction = 'asc') {
    return arr.slice().sort((a, b) => {
        const valueA = columnId(a);
        const valueB = columnId(b);
        const normalize = (val) => {
            if (typeof val === 'number')
                return val;
            if (typeof val === 'string')
                return val.toLowerCase();
            return '';
        };
        const normA = normalize(valueA);
        const normB = normalize(valueB);
        let result;
        if (typeof normA === 'string' && typeof normB === 'string') {
            result = normA.localeCompare(normB);
        }
        else {
            result = normA - normB;
        }
        return direction === 'asc' ? result : -result;
    });
}
/** Infer column order from first-seen keys across all rows */
function inferColumns(rows) {
    const seen = new Set();
    for (const r of rows)
        for (const k of Object.keys(r))
            if (!seen.has(k))
                seen.add(k);
    return Array.from(seen);
}
/** CSV escaping per RFC 4180-ish: quote if value contains comma, quote, or newline */
function csvEscape(v) {
    if (v === undefined)
        return '';
    let s = String(v).replace(/\r\n?/g, '\n');
    const needsQuotes = /[",\n]/.test(s);
    if (needsQuotes)
        s = '"' + s.replace(/"/g, '""') + '"';
    return s;
}
/** Escape for Markdown table cells (pipes + collapse newlines) */
function mdEscapeCell(v) {
    if (v === undefined)
        return '';
    return String(v).replace(/\|/g, '\\|').replace(/\r?\n/g, ' ');
}
/**
 * Convert array of simple objects to CSV string.
 * @param rows Array of objects (Record<string, string|number|undefined>)
 * @param columns Optional explicit column order
 */
export function arrayToCSV(rows, columns) {
    const cols = columns?.slice() ?? inferColumns(rows);
    if (cols.length === 0)
        return '';
    const header = cols.join(',');
    if (rows.length === 0)
        return header + '\n';
    const body = rows.map(r => cols.map(c => csvEscape(r[c])).join(',')).join('\n');
    return header + '\n' + body + '\n';
}
/**
 * Convert array of simple objects to a Markdown pipe table.
 * @param rows Array of objects (Record<string, string|number|undefined>)
 * @param columns Optional explicit column order
 */
export function arrayToMarkdown(rows, columns) {
    const cols = columns?.slice() ?? inferColumns(rows);
    if (cols.length === 0)
        return '';
    const head = '| ' + cols.join(' | ') + ' |';
    const sep = '| ' + cols.map(() => '---').join(' | ') + ' |';
    if (rows.length === 0)
        return head + '\n' + sep + '\n';
    const body = rows
        .map(r => '| ' + cols.map(c => mdEscapeCell(r[c])).join(' | ') + ' |')
        .join('\n');
    return head + '\n' + sep + '\n' + body + '\n';
}
